import _ from 'lodash';

import * as Model from '../model/types';
import {getOpDef, opDefIsLowLevel, unwrapTaggedValues} from '../graph';
import * as HL from '../hl';
import {ServerAPI} from '../serverApi';
import {Cache, MapCache} from '../cache';
import {ResolverContext} from '../resolverContext';
import {
  ForwardGraph,
  ForwardOp,
  newForwardGraph,
  newRefForwardGraph,
} from './forwardGraph';
import {Engine} from './types';
import {Tracer} from '../trace';

export type {Engine} from './types';

async function mapValuesAsync(
  obj: object,
  asyncFn: (value: any, key: string) => Promise<any>
) {
  const promises = Object.entries(obj).map(([key, value]) => {
    return asyncFn(value, key).then(newValue => ({[key]: newValue}));
  });

  const resolvedProms = await Promise.all(promises);
  return resolvedProms.reduce((m, e) => Object.assign(m, e), {});
}

export interface Span {
  addTags(keyValueMap: Record<string, any>): Span;
}
export type BasicEngineTracer = <T>(
  label: string,
  doFn: (span?: Span) => T
) => T;

export interface BasicEngineOpts {
  // By default, we use a hashing forward graph
  fgFactory: () => ForwardGraph;
  engineFactory: () => Engine;

  // Compatible w/ Tracer.trace() from dd-trace
  trace: Tracer;
}
export class BasicEngine implements Engine {
  private readonly opts: BasicEngineOpts;

  constructor(
    private readonly cache: Cache,
    private readonly backend: ServerAPI,

    optsIn?: Partial<BasicEngineOpts>
  ) {
    this.opts = _.defaults({}, optsIn, {
      fgFactory: newForwardGraph,
      engineFactory: this.defaultCreateSubEngine.bind(this),
      trace: (s: string, fn: () => any) => fn(),
    });
  }

  public async executeNodes(
    targetNodes: Model.NodeOrVoidNode[],
    stripTags: boolean = true
  ): Promise<any[]> {
    const fg = this.opts.fgFactory();
    for (const node of targetNodes) {
      if (node.nodeType !== 'void') {
        fg.update(node);
      }
    }
    await this.invalidateRoots(fg);
    await this.executeForward(fg);
    return await this.getNodeResults(fg, targetNodes, stripTags);
  }

  public async mapNode(
    fnNode: Model.Node,
    inputs: any[],
    stripTags?: boolean
  ): Promise<any[]> {
    const vars = HL.filterNodes(
      fnNode,
      checkNode => checkNode.nodeType === 'var'
    ) as Model.VarNode[];
    const varNames = _.uniq(vars.map(v => v.varName));
    if (varNames.length !== 1) {
      throw new Error(
        'mapNode fnNode must have exactly one free variable, but found: ' +
          varNames
      );
    }

    const forwardGraph = this.opts.fgFactory();
    forwardGraph.update(fnNode);
    const context = {
      forwardGraph,
      backend: this.backend,
      frame: {},
      trace: this.opts.trace,
    };

    const result: any[] = [];
    for (const input of inputs) {
      const nodeResults: Map<Model.Node, any> = new Map();
      for (const v of vars) {
        nodeResults.set(v, input);
      }
      await this.executeForwardFast(fnNode, nodeResults, forwardGraph, context);
      result.push(nodeResults.get(fnNode));
    }
    if (stripTags) {
      return unwrapTaggedValues(result);
    }
    return result;
  }

  private async getNodeResults(
    fg: ForwardGraph,
    targetNodes: Model.NodeOrVoidNode[],
    stripTags: boolean = true
  ): Promise<any[]> {
    return await Promise.all(
      targetNodes.map(async node => {
        if (node.nodeType === 'void') {
          return undefined;
        } else if (node.nodeType === 'output') {
          if (stripTags) {
            return unwrapTaggedValues(
              await this.cache.get(fg.getOp(node.fromOp)!.outputNode.node)
            );
          }
          return await this.cache.get(fg.getOp(node.fromOp)!.outputNode.node);
        } else if (node.nodeType === 'const') {
          return node.val;
        }
        throw new Error('invalid targetNode');
      })
    );
  }

  private async invalidateRoots(fg: ForwardGraph): Promise<void> {
    const rootForwardOps = Array.from(fg.getRoots());
    const rootProms = rootForwardOps.map(forwardOp =>
      this.resolveForwardOp(fg, forwardOp)
    );
    const results = await Promise.all(rootProms);
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const forwardOp = rootForwardOps[i];
      const cachedResult = await this.cache.get(forwardOp.outputNode.node);
      // Possibly expensive isEqual check, but only on roots so probably
      // not too bad. apollo-client (which handles all of our root ops as of
      // now) doesn't return reference-equal results even in the cache of an
      // apollo cache hit.
      if (
        typeof cachedResult === 'undefined' ||
        !_.isEqual(cachedResult, result)
      ) {
        // Breaking the pattern and tracing outside of this fn because it's not
        // that interesting.
        await this.clearForwardResults(forwardOp);
        const key = forwardOp.outputNode.node;
        await this.cache.set(key, result);
      }
    }
  }

  private async clearForwardResults(current: ForwardOp): Promise<void> {
    // Traced by caller (uninteresting recursion)
    await Promise.all(
      Array.from(current.outputNode.inputTo).map(nextOp =>
        this.clearForwardResults(nextOp)
      )
    );
    await this.cache.del(current.outputNode.node);
  }

  private async executeForwardFast(
    node: Model.Node,
    nodeResults: Map<Model.Node, any>,
    fg: ForwardGraph,
    context: ResolverContext
  ): Promise<any> {
    // Traced by caller (needs to be fast!)
    let result = nodeResults.get(node);
    if (result !== undefined) {
      return result;
    }
    if (node.nodeType === 'output') {
      const op = node.fromOp;
      const inputs: {[key: string]: any} = {};
      for (const key of Object.keys(node.fromOp.inputs)) {
        inputs[key] = await this.executeForwardFast(
          node.fromOp.inputs[key],
          nodeResults,
          fg,
          context
        );
      }
      const opDef = getOpDef(op.name);
      if (opDefIsLowLevel(opDef)) {
        result = await opDef.resolver(
          inputs,
          fg,
          fg.getOp(op)!,
          context,
          () => this
        );
      }
    } else if (node.nodeType === 'const') {
      result = node.val;
    } else {
      throw new Error('invalid node');
    }
    nodeResults.set(node, result);
    return result;
  }

  private async executeForward(fg: ForwardGraph): Promise<void> {
    // Breadth first search.
    // Only enqueue nodes whose inputs have been resolved.
    const toExecuteOps = new Set(fg.getRoots());
    while (toExecuteOps.size > 0) {
      const executeNowOps = Array.from(toExecuteOps);
      toExecuteOps.clear();

      // Compute set of ops that need a result (those that don't
      // already have a result)
      const needResultIndexes: number[] = [];
      for (let i = 0; i < executeNowOps.length; i++) {
        const forwardOp = executeNowOps[i];
        if (!(await this.cache.has(forwardOp.outputNode.node))) {
          needResultIndexes.push(i);
        }
      }

      const results = await Promise.all(
        needResultIndexes.map(i => this.resolveForwardOp(fg, executeNowOps[i]))
      );

      // Update cache with results
      try {
        await this.cache.setMulti(
          results.map((r, i) => {
            const forwardOp = executeNowOps[needResultIndexes[i]];
            return {
              key: forwardOp.outputNode.node,
              value: r,
            };
          })
        );
      } catch (err) {
        // NOOP
      }

      // Schedule any nodes that now have all of their inputs ready
      for (const forwardOp of executeNowOps) {
        for (const nextForwardOp of forwardOp.outputNode.inputTo) {
          let inputsFilled = true;
          for (const nextForwardOpInputNode of Object.values(
            nextForwardOp.op.inputs
          )) {
            if (nextForwardOpInputNode.nodeType === 'output') {
              const producingForwardOp = fg.getOp(
                nextForwardOpInputNode.fromOp
              );
              if (producingForwardOp == null) {
                throw new Error('Invalid forward op');
              }
              if (!(await this.cache.has(producingForwardOp.outputNode.node))) {
                inputsFilled = false;
                break;
              }
            }
          }

          if (!toExecuteOps.has(nextForwardOp) && inputsFilled) {
            toExecuteOps.add(nextForwardOp);
          }
        }
      }
    }
  }

  private async resolveForwardOp(
    fg: ForwardGraph,
    forwardOp: ForwardOp
  ): Promise<any> {
    const op = forwardOp.op;
    const opDef = getOpDef(op.name);
    const resolver = opDefIsLowLevel(opDef) ? opDef.resolver : null;
    if (resolver == null) {
      throw new Error('no resolver for ' + op.name);
    }
    const opInputs = await mapValuesAsync(op.inputs, inputNode => {
      if (inputNode.nodeType === 'output') {
        const inputForwardOp = fg.getOp(inputNode.fromOp)!;
        return this.cache.get(inputForwardOp.outputNode.node);
      } else if (inputNode.nodeType === 'const') {
        return Promise.resolve(inputNode.val);
      } else {
        console.log('INVALID INPUT IN FORWARD OP', inputNode, forwardOp);
        throw new Error('invalid input in forwardOp');
      }
    });

    try {
      const context = {
        forwardGraph: fg,
        backend: this.backend,
        frame: {},
        trace: this.opts.trace,
      };
      const result = await this.opts.trace(
        `resolver/${op.name}`,
        async () =>
          await resolver(
            opInputs,
            fg,
            forwardOp,
            context,
            this.opts.engineFactory
          )
      );
      // TODO(np): Only need this so forwardOpInputs can read values for
      // output node inputs.
      forwardOp.outputNode.result = result;
      return result;
    } catch (err) {
      const nodeStr = JSON.stringify(forwardOp.outputNode.node, null, 2);
      console.error('Couldnt resolve node: ', nodeStr);
      console.error('Exception from resolver: ', err, (err as Error).stack);
      return Promise.reject();
    }
  }

  private defaultCreateSubEngine(): Engine {
    // Sub-engine uses fast in-memory LRU, not main cache
    return new BasicEngine(new MapCache(), this.backend, {
      fgFactory: newRefForwardGraph,
      trace: this.opts.trace,
    });
  }
}
